import { useEffect, useState } from 'react';
import { FUNDS as QUY_DU_PHONG, FUNDS_GOC } from '../data/funds.js';
import { gopQuy } from './fundModel.js';
import { CO_CAU_HINH, layDb } from './firebase.js';

/**
 * src/lib/useFunds.js — nguồn dữ liệu quỹ cho công cụ khớp hồ sơ.
 *
 * Thứ tự ưu tiên:
 *   1. Firestore collection `funds`  — sửa trong Console, hiện ngay, không deploy
 *   2. src/data/funds.js             — dự phòng, đóng gói sẵn trong bản build
 *
 * Trả về dữ liệu dự phòng NGAY LẬP TỨC rồi mới thay bằng Firestore khi tải
 * xong. Người dùng không phải nhìn màn hình trống chờ mạng, và nếu Firestore
 * lỗi thì họ vẫn dùng được công cụ mà không biết có gì trục trặc.
 *
 * Firestore rules đã cho phép đọc công khai các quỹ `publish.status` là
 * PUBLISHED, nên không cần đăng nhập.
 */

/**
 * Firestore trả về document dạng thuần. Chuẩn hoá tối thiểu để engine dùng
 * được, và bỏ qua document hỏng thay vì để cả trang vỡ vì một bản ghi sai.
 */
function chuanHoa(id, d) {
  if (!d || typeof d !== 'object') return null;
  if (!d.name) return null;
  // Document trong Firestore được seed sớm hơn nên có thể chưa có phần `content`
  // (tóm tắt, quỹ tìm kiếm, lưu ý). Thiếu thì lấy lại từ bản đóng gói, nếu không
  // trang quỹ đã render sẵn sẽ rụng mất mấy mục ngay sau khi hydrate.
  // Chỉ bù phần chữ; thesis/process/provenance vẫn lấy nguyên từ Firestore vì
  // đó là dữ liệu dùng để khớp và để dẫn nguồn.
  const goc = QUY_DU_PHONG.find((f) => f.fundId === (d.fundId || id) || f.slug === (d.slug || id));
  return {
    ...d,
    content: d.content || goc?.content,
    fundId: d.fundId || id,
    slug: d.slug || id,
    thesis: d.thesis || {},
    process: d.process || {},
    provenance: d.provenance || {},
    publish: d.publish || { status: 'PUBLISHED' },
  };
}

export function useFunds() {
  const [state, setState] = useState({
    funds: QUY_DU_PHONG,
    nguon: CO_CAU_HINH ? 'DANG_TAI' : 'DU_PHONG',
    dangTai: CO_CAU_HINH,
    loi: null,
  });

  useEffect(() => {
    if (!CO_CAU_HINH) return undefined;
    let huy = false;

    (async () => {
      try {
        const db = await layDb();
        if (!db) return;
        const { collection, getDocs, query, where } = await import('firebase/firestore/lite');
        // Chỉ hỏi bản đã xuất bản hoặc đã ẩn: Firestore Rules không cho người
        // dùng thường đọc bản nháp, và truy vấn không lọc sẽ bị từ chối cả gói.
        const snap = await getDocs(query(
          collection(db, 'funds'),
          where('publish.status', 'in', ['PUBLISHED', 'HIDDEN']),
        ));

        const ds = snap.docs
          .map((d) => chuanHoa(d.id, d.data()))
          .filter(Boolean);

        if (huy) return;

        // Gộp theo fundId: bản trên Firestore (sửa ở /admin/nguon-von) thay thế
        // hoặc ẩn bản trong code; quỹ chỉ có trong code vẫn hiện.
        if (ds.length === 0) {
          setState({ funds: QUY_DU_PHONG, nguon: 'DU_PHONG', dangTai: false, loi: null });
          return;
        }
        setState({ funds: gopQuy(FUNDS_GOC, ds), nguon: 'FIRESTORE', dangTai: false, loi: null });
      } catch (e) {
        if (huy) return;
        // Lỗi mạng hoặc lỗi quyền: im lặng lùi về dữ liệu đóng gói sẵn. Người
        // dùng vẫn dùng được công cụ; lỗi chỉ ghi ra console cho người phát triển.
        console.warn('Không đọc được quỹ từ Firestore, dùng dữ liệu đóng gói sẵn:', e?.message);
        setState({ funds: QUY_DU_PHONG, nguon: 'DU_PHONG', dangTai: false, loi: e?.message || 'lỗi' });
      }
    })();

    return () => { huy = true; };
  }, []);

  const hienThi = state.funds.filter(
    (f) => (f.publish?.status ?? 'PUBLISHED') === 'PUBLISHED' && !f.isExample
  );

  return {
    ...state,
    soQuyHienThi: hienThi.length,
    danhSachConMong: hienThi.length < 8,
  };
}
